import { Component, computed, effect, inject, signal, ViewChild, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { CoreService, NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import {
  Currency,
  Depot,
  File,
  ProductMove,
  ProductMovePack,
  PurchaseOrder,
  PurchaseOrderItem,
  Supplier,
  User,
} from 'depoto-core/src/entities'
import { ModalStockOperationCardPurchaseComponent } from './components/modal-stock-operation-card-purchase/modal-stock-operation-card-purchase.component'
import { BaseListing } from '../_base-listing/base-listing'
import { FmtDatePipe } from '../../pipes'
import { SchemaUtil, historyBack, getObjectDiff } from '../../utils'
import { productMovePacksPurchaseOrderColumns } from '../../utils/definitions.util'
import { isEqual as areDeeplyEqual } from 'lodash-es'
import { TitleService } from '../../services'
import { DepotService } from '../../services/entity-services/depot.service'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { TagService } from '../../services/entity-services/tag.service'
import {
  injectInfiniteQuery,
  injectMutation,
  injectQuery,
  injectQueryClient,
} from '@tanstack/angular-query-experimental'
import { ProductMovePackService } from '../../services/entity-services/productMovePack.service'
import { PaymentService } from '../../services/entity-services/payment.service'
import { FormControl, FormGroup } from '@angular/forms'
import { PurchaseOrderService } from '../../services/entity-services/purchaseService.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

type SkippedPurchaseOrderProduct = {
  id: number | string
  fullName: string
  code?: string
  ean?: string
  missingLevel: string
}

const purchaseOrderSchema = {
  id: null,
  status: {
    id: null,
    name: null,
  },
  supplier: { id: null, name: null },
  depot: { id: null, name: null },
  tags: { id: null, name: null },
  externalId: null,
  externalReference: null,
  supplierReference: null,
  note: null,
  privateNote: null,
  created: null,
  updated: null,
  number: null,
  dateEstimatedArrival: null,
  createdBy: { id: null, name: null, firstName: null, lastName: null },
  items: {
    id: null,
    quantity: null,
    quantityIn: null,
    purchasePrice: null,
    purchasePriceAll: null,
    currency: {
      id: null,
      name: null,
    },
    note: null,
    product: {
      id: null,
      name: null,
      fullName: null,
      requiredSerialNumbers: null,
      mainImage: {
        url: null,
        thumbnails: {
          format: null,
          url: null,
        },
      },
      purchaseCurrency: {
        id: null,
        name: null,
      },
      code: null,
      ean: null,
    },
    deletable: null,
  },
  files: SchemaUtil.fileExtra,
  pdfUrl: null,
  pdfWOPricesUrl: null,
  excelUrl: null,
  excelWOPricesUrl: null,
  deletable: null,
}

@Component({
  selector: 'app-purchase-order',
  templateUrl: './purchase-order.component.html',
  styleUrls: ['./purchase-order.component.scss'],
  providers: [FmtDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PurchaseOrderComponent extends BaseListing implements OnInit {
  private purchaseService = inject(PurchaseOrderService)
  private depotService = inject(DepotService)
  private tagService = inject(TagService)
  private supplierService = inject(SupplierService)
  private paymentService = inject(PaymentService)
  private productMovePackService = inject(ProductMovePackService)
  private queryClient = injectQueryClient()

  private initialFormValue = signal<any>(null)
  private lastFilledPurchaseOrder: PurchaseOrder | null = null
  submitted = signal(false)
  skippedProductsMissingQuantityLevel = signal<SkippedPurchaseOrderProduct[]>([])
  // Moves queued by the stock-in modal of one row; each stock in is saved right away as its own pack.
  stockInMoves: ProductMove[] = []
  // Stock in goes to the saved order's depot and supplier (unsaved changes block stock in, see canOpenStockIn).
  stockInDepotId = computed(() => Number(this.queryPurchaseOrder.data()?.depot?.id) || 0)
  stockInSupplierId = computed(() => Number(this.queryPurchaseOrder.data()?.supplier?.id) || null)

  // Saving the stock in reloads the order from the server, which would drop unsaved edits on this page.
  readonly canOpenStockIn = (): boolean => {
    if (this.hasUnsavedDataChanges()) {
      alert(this.translateService.instant('purchase-order.stock-in.save-first'))
      return false
    }
    return true
  }

  purchaseOrderForm = new FormGroup({
    status: new FormControl({ value: { id: null, name: null }, disabled: false }, []),
    supplier: new FormControl({ value: new Supplier(), disabled: false }, []),
    // Accept primitive id (string/number) or object to allow setting id directly for select component
    depot: new FormControl<any>({ value: '', disabled: false }, []),
    tags: new FormControl({ value: [], disabled: false }, []),
    externalReference: new FormControl({ value: '', disabled: false }, []),
    supplierReference: new FormControl({ value: '', disabled: false }, []),
    note: new FormControl({ value: '', disabled: false }, []),
    privateNote: new FormControl({ value: '', disabled: false }, []),
    created: new FormControl({ value: '', disabled: false }, []),
    updated: new FormControl({ value: '', disabled: false }, []),
    number: new FormControl({ value: '', disabled: false }, []),
    dateEstimatedArrival: new FormControl({ value: '', disabled: false }, []),
    createdBy: new FormControl({ value: new User(), disabled: false }, []), //{ name: null, firstName: null, lastName: null }
    items: new FormControl({ value: [], disabled: false }, []),
    files: new FormControl({ value: [], disabled: false }, []),
    pdfUrl: new FormControl({ value: '', disabled: false }, []),
    pdfWOPricesUrl: new FormControl({ value: '', disabled: false }, []),
    excelUrl: new FormControl({ value: '', disabled: false }, []),
    excelWOPricesUrl: new FormControl({ value: '', disabled: false }, []),
  })

  @ViewChild(ModalStockOperationCardPurchaseComponent) modalStockOperation: ModalStockOperationCardPurchaseComponent
  isCreating = computed(() => !this.route.snapshot.params['id'])

  // todo
  loading = computed(() => {
    return (
      this.queryPurchaseOrder.isFetching() ||
      this.queryPurchaseOrder.isLoading() ||
      this.queryPurchaseOrder.isPending() ||
      this.mutationCreatePurchaseOrder.isPending() ||
      this.mutationUpdatePurchaseOrder.isPending()
    )
  })
  currentPage: number
  endPage: number
  isEditedFiles = false
  productMovePacks: ProductMovePack[] = []
  listingColumns = signal(productMovePacksPurchaseOrderColumns)
  // storageKey: WritableSignal<{ type: string; key: string }> = signal({ type: '', key: '' })
  sortProp = signal('created')
  sortDir = signal('desc')
  protected areFilesUploading = false

  // Selected tag IDs derived from form value (supports both objects and raw ids)
  // Coerces to the same primitive type as options' id to ensure the multi-select recognizes selection.
  selectedTagIds = computed<any[]>(() => {
    const tags = (this.purchaseOrderForm?.value as any)?.tags || []
    const options = this.queryTags?.data?.() || []
    const sampleId = (Array.isArray(options) && options.length ? (options[0] as any)?.id : undefined) as any
    const wantString = typeof sampleId === 'string'
    const base = (Array.isArray(tags) ? tags : [])
      .map((t: any) => (t && typeof t === 'object' ? t.id : t))
      .filter((v: any) => v !== null && v !== undefined)
    return wantString ? base.map(v => String(v)) : base.map(v => (typeof sampleId === 'number' ? Number(v) : v))
  })

  // Convert any date-like input to HTML date input format (YYYY-MM-DD), using local time
  private toDateInput(value: any): string {
    if (!value) return ''
    let d: Date
    if (value instanceof Date) d = value
    else if (typeof value === 'number') d = new Date(value)
    else if (typeof value === 'string') d = new Date(value)
    else if (typeof value === 'object' && (value as any).toString) d = new Date(String(value))
    else return ''
    if (isNaN(d.getTime())) return ''
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  // Build a human-readable name from a user-like object
  private userDisplayName(user: any): string {
    if (!user) return ''
    const name = (user as any).name
    const first = (user as any).firstName
    const last = (user as any).lastName
    const fromFirstLast = [first, last].filter(Boolean).join(' ').trim()
    return String(name || fromFirstLast || '').trim()
  }

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'purchase_order'),
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  // todo assign as options ???
  readonly queryProductMovePacks = injectInfiniteQuery(() => ({
    queryKey: [
      'productMovePacks',
      Number(this.route.snapshot.params['id']),
      { sortProp: this.sortProp(), sortDir: this.sortDir() },
    ], // todo add  queryKey -> sort, filters, schema - columns
    queryFn: async ({ pageParam }) => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.productMovePackService
          .getProductMovePacks(
            {
              page: pageParam,
              sort: this.sortProp(),
              direction: this.sortDir(),
              filters: {
                type: 'in',
                purchaseOrder: this.route.snapshot.params['id'],
              },
            },
            SchemaUtil.productMovePackList
          )
          .then(result => {
            return result
          })
      } else {
        return { items: [], paginator: { current: 1, endPage: 1 } }
      }
    },
    initialPageParam: 1,
    getNextPageParam: result => {
      return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined
    },
  }))

  readonly queryPurchaseOrder = injectQuery(() => ({
    queryKey: ['purchaseOrder', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = Number(this.route.snapshot.params['id'])
      if (id) {
        return await this.purchaseService.getPurchaseOrder(id, purchaseOrderSchema).then(result => {
          this.fillFormValue(result)
          return result
        })
      } else {
        // Creation mode: ensure form shows default dates right away
        const today = this.toDateInput(new Date())
        const value = {
          ...this.purchaseOrderForm.getRawValue(),
          created: today,
          dateEstimatedArrival: today,
          // Pre-fill createdBy for display after create
          // createdBy: this.userDisplayName(this.core.services.user?.user),
          createdBy: this.core.services.user?.user,
        }
        this.purchaseOrderForm.setValue(value)
        this.initialFormValue.set(structuredClone(value))
        this.purchaseOrderForm.markAsPristine()
        return new PurchaseOrder()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data.id, String(data.number), 'title.purchase-order-id')
      // If still in creation and defaults somehow missing, normalize again
      if (!data?.id) {
        const created = this.toDateInput(this.purchaseOrderForm.value.created) || this.toDateInput(new Date())
        const eta = this.toDateInput(this.purchaseOrderForm.value.dateEstimatedArrival) || created
        const current = this.purchaseOrderForm.getRawValue()
        const value = { ...current, created, dateEstimatedArrival: eta }
        this.purchaseOrderForm.setValue(value)
        this.initialFormValue.set(structuredClone(value))
        this.purchaseOrderForm.markAsPristine()
      }
    },
  }))

  mutationCreatePurchaseOrder = injectMutation(() => ({
    mutationFn: async () => {
      // const result = await this.core.services.purchase.create(this.takePurchaseSnapshot(), { id: null })
      this.submitted.set(true)
      if (this.purchaseOrderForm.invalid) {
        throw new Error('Form is invalid')
        return
      }

      const n = this.getDataToSave()

      // Return the created tag so onSuccess receives the created entity (with id)
      return await this.purchaseService.createPurchaseOrder(n, purchaseOrderSchema).then(result => {
        // After create, if there are files in the form, link them to the newly created order
        if (this.purchaseOrderForm?.value?.files?.length) {
          this.connectUploadedFilesToOrder(result.id, true)
          result.files = n.files
        }
        // this.fillFormValue(result)
        return result
      })
    },
    onSuccess: data => {
      this.submitted.set(false)

      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.purchaseOrderForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.purchaseOrderForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('purchase-order.create-success'))

      // Update the detail cache for this tag so consumers see fresh data immediately
      this.queryClient.setQueryData(['purchaseOrder', Number(data['id'])], data)

      // OPTIONS: keep as-is → update options cache so selects/filters see the new tag immediately
      updateCachedListQueries(this.queryClient, ['purchaseOrders', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['tags', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removePurchaseOrderListings()

      // Optionally keep invalidation for full listing pages
      this.router.navigate(['/purchase-order', data['id']], { replaceUrl: true })
    },
  }))

  mutationUpdatePurchaseOrder = injectMutation(() => ({
    mutationFn: async () => {
      // todo editedfiles
      if (this.isEditedFiles) {
        await this.connectUploadedFilesToOrder()
      }
      // todo const data = this.takePurchaseSnapshot()
      this.submitted.set(true)
      // this.getDirtyValues() // todo ???
      const current = this.purchaseOrderForm.getRawValue() as any
      const initial = this.initialFormValue() as any
      const id = this.queryPurchaseOrder.data()?.id
      const n = this.getDataToSave()
      // todo if (Object.keys(n).some(k => k === 'status')) delete (data as any).status

      return await this.purchaseService.updatePurchaseOrder(n, purchaseOrderSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)

      this.isEditedFiles = false

      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.purchaseOrderForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.purchaseOrderForm.markAsPristine()

      // todo
      this.notificationService.success(this.translateService.instant('purchase-order.update-success'))

      // Update the detail cache for this tag with returned server data
      this.queryClient.setQueryData(['purchaseOrder', data['id']], data)

      // OPTIONS: keep as-is → update options cache value for this tag
      updateCachedListQueries(this.queryClient, ['purchaseOrders', 'options'], data, { mode: 'update' })

      // LISTING ['tags', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removePurchaseOrderListings()
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  deletePurchaseOrder = injectMutation(() => ({
    mutationFn: async () => {
      if (
        !confirm(
          this.translateService.instant('purchase-order.delete-confirm', { id: this.queryPurchaseOrder.data().number })
        )
      ) {
        return
      }

      this.getDirtyValues()

      if (this.purchaseOrderForm.value.files?.length) {
        for (const file of this.purchaseOrderForm.value.files) {
          await this.core.services.file.delete({ id: file.id })
        }
      }

      // await this.purchaseService.deletePurchaseOrder({ id: Number(this.router.url.split('/')[2]) }).then(result => {
      await this.purchaseService.deletePurchaseOrder(Number(this.router.url.split('/')[2])).then(result => {
        // this.pristinePurchase = this.takePurchaseSnapshot() todo delete?
        this.notificationService.success(this.translateService.instant('purchase-order.delete-success'))
        const idStr = String(this.router.url.split('/')[2])
        // OPTIONS: keep as-is → update options cache by removing the tag
        updateCachedListQueries(this.queryClient, ['purchaseOrders', 'options'], { id: Number(idStr) } as any, {
          mode: 'delete',
        })

        // LISTING ['tags', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
        this.removeFromListingsIfPresent(Number(idStr))
        // Optionally refresh listing pages
        // todo this.queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })

        this.router.navigate(['/purchase-orders'], { replaceUrl: true })
      })
    },
    // todo if error  .catch(() => this.notificationService.error(this.translateService.instant('purchase-order.delete-error')))
    enabled: !!this.router.url.split('/')[2],
  }))

  constructor(
    protected core: CoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fmtDatePipe: FmtDatePipe,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
    private titleService: TitleService
  ) {
    super(core)
    effect(() => {
      const pO = this.queryPurchaseOrder.data()
      const id = this.route.snapshot.params['id']
      // Refill the form only when the order data itself changes. This effect also re-runs when the moves list,
      // tags, suppliers, currencies or depots load, and refilling then would put back the cached order and drop
      // edits and refreshed stocked-in quantities.
      if (id && pO && pO !== this.lastFilledPurchaseOrder) {
        this.lastFilledPurchaseOrder = pO
        this.fillFormValue(pO)
      }
      this.queryProductMovePacks.data()
      this.queryTags.data()
      this.querySuppliers.data()
      this.queryCurrencies.data()
      this.queryDepotsOptions.data()
      this.queryProductMovePacks.data()
    })

    // When tag options load, ensure the form 'tags' control holds the actual option objects.
    // This guarantees the multi-select resolves and displays selected tag names immediately
    // after create or navigation, even if the server returned plain objects or ids.
    effect(() => {
      const options = this.queryTags.data()
      const formVal: any = this.purchaseOrderForm?.value
      const formTags: any[] = (formVal && Array.isArray(formVal.tags) ? formVal.tags : []) as any[]
      if (!options || !options.length || !formTags.length) return

      const formIds = formTags
        .map((t: any) => (t && typeof t === 'object' ? t.id : t))
        .filter((v: any) => v !== null && v !== undefined)
        .map((v: any) => String(v))

      if (!formIds.length) return

      const mapped = options.filter(opt => formIds.includes(String((opt as any)?.id)))

      // Compare sets of ids; only set when they differ from current
      const sort = (arr: string[]) => [...arr].sort()
      const idsEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false
        const sa = sort(a)
        const sb = sort(b)
        for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false
        return true
      }

      const mappedIds = mapped.map(m => String((m as any)?.id))
      if (!idsEqual(mappedIds, formIds)) {
        this.purchaseOrderForm.get('tags')?.setValue(mapped as any)
      }
    })

    // Normalize depot control to a primitive id once options are available (create mode only)
    effect(() => {
      const isCreate = !this.route.snapshot.params['id']
      if (!isCreate) return
      const options = this.queryDepotsOptions.data()
      if (!options || !options.length) return
      const cur: any = this.purchaseOrderForm?.value?.depot
      if (!cur) return
      const curId = typeof cur === 'object' ? cur.id : cur
      if (curId === undefined || curId === null) return
      const match = options.find(d => String((d as any)?.id) === String(curId))
      // Our select component expects primitive ids (not objects) when bindToObject is false.
      // Ensure the control holds the id so the option is shown as selected.
      if (match) this.purchaseOrderForm.get('depot')?.setValue(String((match as any).id))
    })
  }

  // Initialize data from router navigation state when creating a new purchase order
  // This allows `/products` to pass preselected items and depot via Router.navigate(..., { state })
  // and have them show up immediately in the create form.
  ngOnInit(): void {
    // Prefer the current navigation extras during the initial navigation, with a fallback to history.state
    const navState: any =
      this.router.getCurrentNavigation()?.extras?.state || (typeof history !== 'undefined' ? history.state : {}) || {}

    // Only prefill in create mode (no id param in the route)
    const isCreate = !this.route.snapshot.params['id']
    if (isCreate && navState) {
      const skippedProducts = Array.isArray(navState.excludedProductsMissingQuantityLevel)
        ? navState.excludedProductsMissingQuantityLevel
        : []
      this.skippedProductsMissingQuantityLevel.set(skippedProducts)

      const selected = Array.isArray(navState.selectedProducts) ? navState.selectedProducts : []
      if (selected.length) {
        selected.forEach((it: any) => this.addProduct(it))
      }

      // If depot id was provided, set it now; it will be normalized to the full option below when options load
      if (navState.depot !== undefined && navState.depot !== null) {
        const depotOptions = this.queryDepotsOptions?.data?.() || []
        const match = depotOptions.find(d => String((d as any)?.id) === String(navState.depot))
        // form-row-select expects option values as strings; set string id so the option renders selected
        this.purchaseOrderForm.get('depot')?.setValue(String(match ? (match as any).id : navState.depot))
      }
    }
  }

  hasUnsavedDataChanges(): boolean {
    const initial = this.initialFormValue() as any

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.purchaseOrderForm.dirty
    }

    // getDataToSave() holds only changed fields, plus id, supplier and depot, which it always sends.
    const { id, supplier, depot, ...changed } = this.getDataToSave()
    const toId = (v: any) => String((v && typeof v === 'object' ? v.id : v) ?? '')
    return (
      Object.keys(changed).length > 0 ||
      toId(supplier) !== toId(initial.supplier) ||
      toId(depot) !== toId(initial.depot)
    )
  }

  goBack(): void {
    historyBack()
  }

  getThumbUrl(image: File): string {
    return image?.thumbnails.filter(t => t.format === 'w135wp')[0]?.url
  }

  public addProduct(item: PurchaseOrderItem): void {
    const incomingCurrencyId = typeof item.currency === 'string' ? item.currency : (item.currency as Currency)?.id
    const itemProdId = typeof (item as any)?.product === 'object' ? (item as any)?.product?.id : (item as any)?.product

    const existingIndex = this.purchaseOrderForm.value.items.findIndex(p => {
      const pProdId = typeof (p as any)?.product === 'object' ? (p as any)?.product?.id : (p as any)?.product
      return (
        String(pProdId) === String(itemProdId) &&
        Number(p.purchasePrice) === Number(item.purchasePrice) &&
        String(p.note || '') === String(item.note || '') &&
        String(p.product.purchaseCurrency?.id || '') === String(incomingCurrencyId || '')
      )
    })

    if (existingIndex > -1) {
      const existing = this.purchaseOrderForm.value.items[existingIndex]
      const newQuantity = Number(existing.quantity) + Number(item.quantity)
      const updated: PurchaseOrderItem = {
        ...existing,
        quantity: newQuantity,
        purchasePriceAll: Number(newQuantity) * Number(existing.purchasePrice),
      }
      // Update immutably since 'items' is a FormControl holding an array (not a FormArray)
      const currentItems = (this.purchaseOrderForm.value.items || []) as PurchaseOrderItem[]
      const nextItems = currentItems.map((it, i) => (i === existingIndex ? updated : it))
      this.purchaseOrderForm.get('items')?.setValue(nextItems)
    } else {
      // Ensure product is stored as an object so the table can render name, code, ean, and image immediately
      const productObject =
        typeof (item as any)?.product === 'object'
          ? (item as any).product
          : {
              id: (item as any)?.product,
              fullName: (item as any)?.fullName,
              mainImage: (item as any)?.mainImage,
              code: (item as any)?.code,
              ean: (item as any)?.ean,
            }

      const newnew = {
        customData: item.customData,
        externalId: item.externalId,
        // id: item.id,
        note: item.note || '',
        product: productObject,
        productMoves: item.productMoves,
        purchasePrice: item.purchasePrice,
        purchasePriceAll: item.quantity * item.purchasePrice,
        purchasePriceAllWithVat: item.purchasePriceAllWithVat,
        purchasePriceWithVat: item.purchasePriceAllWithVat,
        quantity: item.quantity,
        quantityIn: item.quantityIn || 0,
        status: item.status,
        // ensure currency is carried over so it is later sent to server
        currency: item.currency,
      }

      // Append the new item to the existing array immutably
      const currentItems = (this.purchaseOrderForm.value.items || []) as PurchaseOrderItem[]
      const nextItems = [...currentItems, newnew as any]
      this.purchaseOrderForm.get('items')?.setValue(nextItems)

      // this.productList.push({ ...item, purchasePriceAll: item.quantity * item.purchasePrice })
    }
  }

  getcurrency(item) {
    const currencyId = typeof item?.currency === 'string' ? item.currency : item?.currency?.id
    const list = this.queryCurrencies?.data?.()
    // If currency list not yet loaded, try to return existing object or a minimal object with id
    if (!list) {
      if (typeof item?.currency === 'object' && item?.currency?.id) return item.currency
      if (currencyId) return { id: currencyId } as any
      return null
    }
    return (
      list.find(c => c.id === currencyId) ||
      (typeof item?.currency === 'object' && item?.currency?.id
        ? item.currency
        : currencyId
          ? ({ id: currencyId } as any)
          : null)
    )
  }

  fileAdded(file: File) {
    if (this.queryPurchaseOrder.data().id) {
      // save with order's id
      try {
        this.core.services.file
          .update(file, null, null, this.queryPurchaseOrder.data().id, null, null, SchemaUtil.fileExtra)
          .then(createdFile => {
            // todo
            this.purchaseOrderForm.value.files.push(createdFile)
          })
        this.isEditedFiles = false
      } catch (error) {
        console.error('Error in function connectUploadedFilesToOrder. Details: ', error.message)
      }
    } else {
      // add file to not created order
      this.purchaseOrderForm.value.files.push(file)
    }
  }

  public edit(item, event) {
    // Ensure we update the actual element in productList instead of reassigning the local parameter
    const idx = this.purchaseOrderForm.value.items.findIndex(p => p === item || (item?.id && p?.id === item?.id))
    if (idx === -1) {
      return
    }

    const existing = this.purchaseOrderForm.value.items[idx]

    const nextQuantity = Number(event?.quantity ?? existing.quantity) || 0
    const nextPrice = Number(event?.purchasePrice ?? existing.purchasePrice) || 0

    const updated: PurchaseOrderItem = {
      ...existing,
      // keep existing.product (object) and identifiers
      quantity: nextQuantity,
      purchasePrice: nextPrice,
      purchasePriceAll: nextQuantity * nextPrice,
      note: event?.note ?? existing.note,
      currency: event?.currency ?? existing.currency,
    }

    // Update the array held inside the 'items' FormControl immutably
    const currentItems = (this.purchaseOrderForm.value.items || []) as PurchaseOrderItem[]
    const nextItems = currentItems.map((it, i) => (i === idx ? updated : it))
    this.purchaseOrderForm.get('items')?.setValue(nextItems)
  }

  // Saved rows with a product that are not fully stocked in yet. Products with serial numbers are left out:
  // the stock in is saved right away here, without the serial-number step of the bulk stock page.
  canStockIn(item: PurchaseOrderItem): boolean {
    return (
      !this.isCreating() &&
      Number(item?.id) > 0 &&
      Number(item?.product?.id) > 0 &&
      !item.product?.requiredSerialNumbers &&
      this.getStockInQuantity(item) > 0
    )
  }

  // The quantity still missing on the row.
  getStockInQuantity(item: PurchaseOrderItem): number {
    return Math.max(Number(item?.quantity || 0) - Number(item?.quantityIn || 0), 0)
  }

  async createStockIn(item: PurchaseOrderItem, moves: ProductMove[]): Promise<void> {
    // Take this row's moves out of the shared queue now, so a stock in started before this one is saved
    // does not send them again.
    const queuedMoves = [...(moves ?? [])]
    this.stockInMoves.length = 0
    const purchaseOrderId = Number(this.queryPurchaseOrder.data()?.id)
    if (!queuedMoves.length || !purchaseOrderId) return
    try {
      // Linking each move to the order row is what raises the row's "done" quantity.
      queuedMoves.forEach(move => (move.purchaseOrderItem = new PurchaseOrderItem({ id: item.id })))
      const movePack = new ProductMovePack({
        type: { id: 'in' },
        purchaseOrder: { id: purchaseOrderId },
        moves: queuedMoves,
      } as any)
      const result = await this.core.services.productMovePack.create(movePack, { id: null })
      if (!(Number(result?.id) > 0)) {
        this.notificationService.error(this.translateService.instant('modal-stock-load.error'))
        return
      }
      this.notificationService.success(this.translateService.instant('alert.loading.stock'))
      await Promise.all([
        this.refreshQuantitiesIn(purchaseOrderId),
        this.queryClient.invalidateQueries({ queryKey: ['productMovePacks', purchaseOrderId] }),
      ])
    } catch (err) {
      console.error(err)
      this.notificationService.error(this.translateService.instant('modal-stock-load.error'))
    }
  }

  // Updates only the rows' stocked-in quantities. Reloading the whole order would refill the form and drop
  // anything edited while the stock in was being saved. quantityIn is not part of the unsaved-changes check.
  private async refreshQuantitiesIn(purchaseOrderId: number): Promise<void> {
    // Only the ids and quantityIn are selected; the service types its schema after the full default one.
    const fresh = await this.purchaseService.getPurchaseOrder(purchaseOrderId, {
      id: null,
      items: { id: null, quantityIn: null },
    } as any)
    const quantityInById = new Map((fresh?.items ?? []).map(it => [Number(it.id), it.quantityIn]))
    const withFreshQuantityIn = (items: PurchaseOrderItem[] = []) =>
      items.map(it =>
        quantityInById.has(Number(it?.id)) ? { ...it, quantityIn: quantityInById.get(Number(it.id)) } : it
      )

    this.purchaseOrderForm.get('items')?.setValue(withFreshQuantityIn(this.purchaseOrderForm.value.items) as any)
    const initial = this.initialFormValue()
    if (initial) {
      this.initialFormValue.set({ ...initial, items: withFreshQuantityIn(initial.items) })
    }
  }

  public async remove(item: PurchaseOrderItem) {
    if (item.id) {
      if (!confirm(this.translateService.instant('purchase-order.product.delete-confirm', { id: item.id }))) {
        return
      }
      await this.core.services.purchase
        .deletePurchaseOrderItem(item.id)
        .then(() => {
          this.removeProductFromList(item)
          this.notificationService.success(this.translateService.instant('purchase-order.product.delete-success'))
        })
        .catch(err => {
          console.error(err)
          this.notificationService.error(this.translateService.instant('purchase-order.product.delete-error'))
        })
    } else {
      this.removeProductFromList(item)
    }
  }

  private removeProductFromList(item: PurchaseOrderItem): void {
    const getCurrencyId = (cur: string | Currency | undefined) => (typeof cur === 'string' ? cur : cur?.id)

    const isSame = (a: PurchaseOrderItem, b: PurchaseOrderItem) =>
      a.product?.code === b.product?.code &&
      getCurrencyId(a.currency?.id) === getCurrencyId(b.currency?.id) &&
      String(a.product?.ean) === String(b.product?.ean) &&
      String(a.product?.fullName) === String(b.product?.fullName) &&
      a.product?.mainImage === b.product?.mainImage &&
      Number(a.product?.id) === Number(b.product?.id) &&
      Number(a.purchasePrice) === Number(b.purchasePrice) &&
      Number(a.purchasePriceAll) === Number(b.purchasePriceAll) &&
      Number(a.quantity) === Number(b.quantity)

    const indexToRemove = this.purchaseOrderForm.value.items.findIndex(value => isSame(value, item))
    if (indexToRemove === -1) {
      return
    }

    // Create a new array without the matched item (remove only the first match)
    this.purchaseOrderForm
      .get('items')
      .setValue(this.purchaseOrderForm.value.items.filter((_, index) => index !== indexToRemove))
    // this.productList = this.productList.filter((_, index) => index !== indexToRemove)
  }

  private async connectUploadedFilesToOrder(id = this.queryPurchaseOrder.data().id, refetch = false) {
    if (
      !this.purchaseOrderForm.value.files ||
      (this.purchaseOrderForm.value.files && this.purchaseOrderForm.value.files.length === 0)
    ) {
      return
    }
    try {
      const promises = this.purchaseOrderForm.value.files.map(file =>
        this.core.services.file.update(file, null, null, id, null, null, SchemaUtil.fileExtra).then(r => {
          // Append the returned file object to the existing files array (not spread, r is not iterable)
          const current = Array.isArray(this.purchaseOrderForm.value.files)
            ? this.purchaseOrderForm.value.files.filter(Boolean)
            : []
          this.purchaseOrderForm.get('files')?.setValue([...current, r])
        })
      )
      await Promise.all(promises)
      this.isEditedFiles = false
    } catch (error) {
      console.error('Error in function connectUploadedFilesToOrder. Details: ', error.message)
    }
  }

  public fillFormValue(purchaseOrder) {
    const value = {
      status: purchaseOrder?.status?.id || null,
      supplier: purchaseOrder?.supplier?.id || null,
      depot: purchaseOrder?.depot?.id || null,
      tags: purchaseOrder?.tags || [],
      externalReference: purchaseOrder.externalReference || '',
      supplierReference: purchaseOrder.supplierReference || '',
      note: purchaseOrder.note || '',
      privateNote: purchaseOrder.privateNote || '',
      created: this.toDateInput(purchaseOrder.created) || '',
      updated: this.toDateInput(purchaseOrder.updated) || '',
      number: purchaseOrder.number || '',
      dateEstimatedArrival: this.toDateInput(purchaseOrder.dateEstimatedArrival) || '',
      createdBy: purchaseOrder?.createdBy || null,
      items: purchaseOrder?.items || [],
      files: purchaseOrder?.files || [],
      pdfUrl: purchaseOrder?.pdfUrl || '',
      pdfWOPricesUrl: purchaseOrder?.pdfWOPricesUrl || '',
      excelUrl: purchaseOrder?.excelUrl || '',
      excelWOPricesUrl: purchaseOrder?.excelWOPricesUrl || '',
    }

    this.purchaseOrderForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.purchaseOrderForm.markAsPristine()
  }

  private removePurchaseOrderListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['purchaseOrders'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  getDirtyValues(form = this.purchaseOrderForm) {
    const dirtyValues = {}

    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key]

      if (currentControl.dirty) {
        if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl)
        else dirtyValues[key] = currentControl.value
      }
    })

    return dirtyValues
  }

  // ===== Helpers for cache handling of tag listings =====
  private isPurchaseOrdersListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'purchaseOrders' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['purchaseOrders'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isPurchaseOrdersListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }

  getDataToSave() {
    const current = this.purchaseOrderForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    // const routeId = this.route.snapshot.params['id']
    const routeId = this.queryPurchaseOrder.data()?.id
    if (routeId) patch.id = Number(routeId)
    const isCreate = !routeId

    // Always include required fields (normalize to ids if objects are present)
    const toId = (v: any) => (v && typeof v === 'object' ? v.id : v)
    patch.supplier = toId(current.supplier)
    patch.depot = toId(current.depot)

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr)
        ? arr.map((x: any) => (x && typeof x === 'object' ? x.id : x)).filter((v: any) => v !== undefined && v !== null)
        : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (norm(current.company) !== norm(initial?.company)) {
      patch.company = current.company
    }
    if (norm(current.note) !== norm(initial?.note)) {
      patch.note = current.note
    }
    if (norm(current.privateNote) !== norm(initial?.privateNote)) {
      patch.privateNote = current.privateNote
    }
    // On CREATE the field is pre-filled with today's date before initialFormValue is snapshotted,
    // so it never looks "changed" - always send it when creating.
    if (isCreate || norm(current.dateEstimatedArrival) !== norm(initial?.dateEstimatedArrival)) {
      patch.dateEstimatedArrival = current.dateEstimatedArrival
    }
    if (norm(current.externalId) !== norm(initial?.externalId)) {
      patch.externalId = current.externalId
    }
    if (norm(current.externalReference) !== norm(initial?.externalReference)) {
      patch.externalReference = current.externalReference
    }
    if (norm(current.supplierReference) !== norm(initial?.supplierReference)) {
      patch.supplierReference = current.supplierReference
    }
    if (norm(current.status) !== norm(initial?.status)) {
      patch.status = current.status
    }
    if (norm(current.customData) !== norm(initial?.customData)) {
      patch.customData = current.customData
    }

    // Arrays: items
    // Always include items on CREATE (no id yet)
    const currentItems = Array.isArray(current.items) ? current.items : []
    const initialItems = Array.isArray(initial?.items) ? initial?.items : []

    const getId = (x: any) => (x && typeof x === 'object' ? x.id : x)
    const getProdId = (x: any) => (x && typeof x === 'object' ? x.id : x)
    const getCurId = (x: any) => (x && typeof x === 'object' ? x.id : x)

    const itemsDifferShallow = (() => {
      if (currentItems.length !== initialItems.length) return true
      for (let i = 0; i < currentItems.length; i++) {
        const a = currentItems[i] || {}
        const b = initialItems[i] || {}
        const aId = getId(a?.id)
        const bId = getId(b?.id)
        const aProd = getProdId(typeof a?.product === 'object' ? a?.product?.id : a?.product)
        const bProd = getProdId(typeof b?.product === 'object' ? b?.product?.id : b?.product)
        const aQty = Number(a?.quantity ?? 0)
        const bQty = Number(b?.quantity ?? 0)
        const aPrice = Number(a?.purchasePrice ?? 0)
        const bPrice = Number(b?.purchasePrice ?? 0)
        const aNote = String(a?.note ?? '')
        const bNote = String(b?.note ?? '')
        const aCur = getCurId(typeof a?.currency === 'object' ? a?.currency?.id : a?.currency)
        const bCur = getCurId(typeof b?.currency === 'object' ? b?.currency?.id : b?.currency)
        if (
          !(aId === bId && aProd === bProd && aQty === bQty && aPrice === bPrice && aNote === bNote && aCur === bCur)
        ) {
          return true
        }
      }
      return false
    })()

    const hasNewItem = currentItems.some((it: any) => !(Number(it?.id ?? 0) > 0))

    if (isCreate || hasNewItem || itemsDifferShallow || !eqArrayIds(current.items, initial?.items)) {
      // Normalize items to API shape to ensure currency/product are ids and numbers are coerced
      const normalizedItems = currentItems.map((it: any) => {
        const prodId = typeof it?.product === 'object' ? it?.product?.id : it?.product
        // Resolve currency id with robust fallbacks for existing items that weren't edited
        const directCurId = typeof it?.currency === 'object' ? it?.currency?.id : it?.currency
        let curId = directCurId
        if (!curId) {
          // 1) Try to take currency from the initially loaded item with the same id
          const itId = Number(it?.id ?? 0)
          if (itId > 0) {
            const initialMatch = initialItems.find((ini: any) => Number(ini?.id ?? 0) === itId)
            if (initialMatch) {
              const iniCurId =
                typeof initialMatch?.currency === 'object' ? initialMatch?.currency?.id : initialMatch?.currency
              if (iniCurId) curId = iniCurId
            }
          }
        }
        if (!curId) {
          // 2) Try to resolve via current currencies list using helper (returns option/object)
          const resolved = this.getcurrency(it) as any
          if (resolved && typeof resolved === 'object' && resolved.id) curId = resolved.id
        }
        if (!curId) {
          // 3) Fallback to company currency id
          const companyCurId = this.core?.services?.user?.user?.company?.currency?.id
          if (companyCurId) curId = companyCurId
        }
        const out: any = {
          product: prodId,
          quantity: Number(it?.quantity ?? 0),
        }
        if (Number(it?.id ?? 0) > 0) out.id = it.id
        if (it?.purchasePrice !== undefined && it?.purchasePrice !== null && it?.purchasePrice !== '') {
          out.purchasePrice = Number(it.purchasePrice)
        }
        if (Object.prototype.hasOwnProperty.call(it, 'note')) {
          out.note = it.note
        }
        if (curId !== undefined && curId !== null && curId !== '') {
          out.currency = curId
        }
        if (Object.prototype.hasOwnProperty.call(it, 'externalId') && it?.externalId) {
          out.externalId = it.externalId
        }
        if (Object.prototype.hasOwnProperty.call(it, 'customData') && it?.customData) {
          out.customData = it.customData
        }
        return out
      })
      patch.items = normalizedItems
    }
    const eqArrayAsSet = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.slice().sort() : []
      const bb = Array.isArray(b) ? b.slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (String(aa[i]) !== String(bb[i])) return false
      return true
    }

    // TAGS: compare by ids, but send FULL OBJECTS (backend maps to ids later)
    const currentTagIds = idArray(current.tags)
    const initialTagIds = idArray(initial?.tags)
    if (isCreate) {
      // On create: include the array of tag objects (may be empty array)
      patch.tags = Array.isArray(current.tags) ? current.tags : []
    } else if (!eqArrayAsSet(currentTagIds, initialTagIds)) {
      // On update: include full objects only if the set of ids changed
      patch.tags = Array.isArray(current.tags) ? current.tags : []
    }

    // FILES: do NOT send files in patch. They are handled via connectUploadedFilesToOrder()
    // Keeping this intentionally empty.

    return patch
  }

  purchaseOrderFormChanged(event) {
    // todo
    this.purchaseOrderForm.setValue(event)
  }

  tagsChanged(event) {
    this.purchaseOrderForm.get('tags').setValue(this.queryTags.data().filter(t => event.includes(t.id)))
  }

  download(type: string) {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.purchaseOrderForm.value[type])
  }
}
